import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { createNotification, updateNotification } from "api/layer";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageType as FormData } from "../type";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en"; // 导入中文语言包
import "dayjs/locale/zh"; // 导入中文语言包

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  data?: FormData;
  releaseVersions?: { id: number; version: string }[];
}

// Internal form data type with Dayjs objects
type FormDataInternal = Omit<FormData, "startTime" | "expirationTime"> & {
  startTime: Dayjs | null;
  expirationTime: Dayjs | null;
  releaseVersion?: string;
  releaseId?: number;
  status: "draft" | "publish";
};

function MessageEditModal(props: Props) {
  const [t] = useTranslation();
  const [context, dispatch] = useContext(AppContext);
  const [isError, setIsError] = useState(false);

  const [formData, setFormData] = useState<FormDataInternal>(
    props.data
      ? {
          ...props.data,
          startTime: props.data.startTime ? dayjs(props.data.startTime) : null,
          expirationTime: props.data.expirationTime
            ? dayjs(props.data.expirationTime)
            : null,
          releaseVersion: props.data.releaseVersion || "",
          releaseId: props.data.releaseId,
          status: props.data.status || "draft",
          isRing: props.data.isRing ?? false,
        }
      : {
          notificationId: "",
          notificationType: "",
          notificationMessage: "",
          startTime: null,
          expirationTime: null,
          noExpirationTime: false,
          isBanner: false,
          isRing: false,
          releaseVersion: "",
          releaseId: undefined,
          status: "draft",
        }
  );

  const handleChange = (event: any) => {
    const { name, value, type, checked } = event.target;

    if (name === "noExpirationTime" && checked) {
      setFormData((prevData: any) => ({
        ...prevData,
        noExpirationTime: true,
        startTime: null,
        expirationTime: null,
      }));
    } else {
      setFormData((prevData: any) => ({
        ...prevData,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const close = () => {
    props.onClose();
  };

  const maxLength = formData.isBanner ? 130 : 80;
  const isContentOverLimit = formData.notificationMessage.length > maxLength;

  const handleSave = () => {
    // Check content length limit
    if (isContentOverLimit) {
      setIsError(true);
      return;
    }

    // At least one of isBanner or isRing must be checked
    if (!formData.isBanner && !formData.isRing) {
      setIsError(true);
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "addNotification",
          titleIconColor: "yellow",
          titleIcon: "warning",
          resMessage: t("userSystem.mustSelectBannerOrRing"),
        },
      });
      return;
    }

    // 如果没有勾选无期限，则日期必填
    if (!formData.noExpirationTime) {
      if (
        !formData.notificationType ||
        !formData.startTime ||
        !formData.expirationTime
      ) {
        setIsError(true);
        return;
      }
    } else {
      if (!formData.notificationType) {
        setIsError(true);
        return;
      }
    }

    // 如果类型是Release，则releaseVersion必填
    if (formData.notificationType === "Release") {
      if (!props.releaseVersions || props.releaseVersions.length === 0) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "addNotification",
            titleIconColor: "yellow",
            titleIcon: "warning",
            resMessage: t("snackBar.noReleaseVersionAvailable"),
          },
        });
        return;
      }
      if (!formData.releaseVersion) {
        setIsError(true);
        return;
      }
    }

    let data: any = {
      ...formData,
      releaseId:
        formData.notificationType === "Release"
          ? formData.releaseId
          : undefined,
      releaseVersion:
        formData.notificationType === "Release"
          ? formData.releaseVersion
          : undefined,
    };

    // Only include date fields if noExpirationTime is false
    if (!formData.noExpirationTime) {
      data.startTime = formData.startTime
        ? dayjs(formData.startTime).format("YYYY-MM-DD HH:mm:ss")
        : "";
      data.expirationTime = formData.expirationTime
        ? dayjs(formData.expirationTime).format("YYYY-MM-DD HH:mm:ss")
        : "";
    } else {
      // Remove date fields when unlimited time is selected
      delete data.startTime;
      delete data.expirationTime;
    }

    if (props.data) {
      updateNotification(data)
        .then((res: any) => {
          if (res && res.code === 200) {
            props.onClose();
            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "editNotification",
                message: "editNotificationSuccess",
              },
            });
          } else {
            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "editNotification",
                titleIconColor: "yellow",
                titleIcon: "warning",
                resMessage: res.message.includes(
                  "由于关联的发布信息不是已发布的状态"
                )
                  ? t("snackBar.editNotificationFailBecauseRelatedRelease")
                  : res.message.includes("该发布信息已关联其他通知")
                    ? t("snackBar.addNotificationFailBecauseRelatedRelease")
                    : t("snackBar.editNotificationFail"),
              },
            });
          }
        })
        .finally(() => {
          props.onSave();
        });
    } else {
      createNotification(data)
        .then((res: any) => {
          if (res && res.code === 200) {
            props.onClose();
            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "addNotification",
                message: "addNotificationSuccess",
              },
            });
          } else {
            dispatch({
              type: AppAction.addSnackBarParams,
              payload: {
                title: "addNotification",
                titleIconColor: "yellow",
                titleIcon: "warning",
                resMessage: res.message.includes("该发布信息已关联其他通知")
                  ? t("snackBar.addNotificationFailBecauseRelatedRelease")
                  : t("snackBar.addNotificationFail"),
              },
            });
          }
        })
        .finally(() => {
          props.onSave();
        });
    }
  };

  const reset = () => {
    setFormData(
      props.data
        ? {
            ...props.data,
            startTime: props.data.startTime
              ? dayjs(props.data.startTime)
              : null,
            expirationTime: props.data.expirationTime
              ? dayjs(props.data.expirationTime)
              : null,
            releaseVersion: props.data.releaseVersion || "",
            releaseId: props.data.releaseId,
            status: props.data.status || "draft",
            isRing: props.data.isRing ?? false,
          }
        : {
            notificationId: "",
            notificationType: "",
            notificationMessage: "",
            startTime: null,
            expirationTime: null,
            noExpirationTime: false,
            isBanner: false,
            isRing: false,
            releaseVersion: "",
            releaseId: undefined,
            status: "draft",
          }
    );
  };

  return (
    <Dialog open={props.open} onClose={close}>
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "16px",
          opacity: 0.79,
          color: "#333",
          width: "600px",
        }}
      >
        {props.data ? t("userSystem.editMessage") : t("userSystem.addMessage")}
      </DialogTitle>
      <DialogContent
        sx={{
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
          paddingBottom: "0",
        }}
      >
        <div className="flex gap-4 items-center flex-wrap">
          <FormControl
            className="translate-y-1 flex-shrink-0"
            error={isError && formData.notificationType.length === 0}
            sx={{ minWidth: 180 }}
          >
            <InputLabel size="small" color="success" required>
              {t("userSystem.messageType")}
            </InputLabel>
            <Select
              size="small"
              value={formData.notificationType || ""}
              onChange={handleChange}
              name="notificationType"
              label={t("userSystem.messageType")}
              color="success"
            >
              {[
                {
                  value: "System notification",
                  label: t("userSystem.notificationTypeSystemNotification"),
                },
                {
                  value: "Release",
                  label: t("userSystem.notificationTypeRelease"),
                },
                {
                  value: "Welcome",
                  label: t("userSystem.notificationTypeWelcome"),
                },
              ].map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <ListItemText primary={type.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={context.language === "cn" ? "zh" : "en"}
          >
            <DatePicker
              label={t("userSystem.startDate") + " *"}
              value={formData.startTime as any}
              format="YYYY-MM-DD"
              disabled={formData.noExpirationTime}
              onChange={(event) => {
                handleChange({
                  target: { name: "startTime", value: event },
                });
              }}
              slotProps={{
                textField: {
                  margin: "normal",
                  size: "small",
                  color: "success",
                  sx: { width: 160 },
                  error:
                    isError &&
                    !formData.startTime &&
                    !formData.noExpirationTime,
                },
              }}
              sx={{
                "& .MuiInputLabel-root": {
                  color:
                    isError && !formData.startTime && !formData.noExpirationTime
                      ? "#d32f2f"
                      : "",
                },
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    isError && !formData.startTime && !formData.noExpirationTime
                      ? "#d32f2f"
                      : "",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color:
                    isError && !formData.startTime && !formData.noExpirationTime
                      ? "#d32f2f"
                      : "green",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      isError &&
                      !formData.startTime &&
                      !formData.noExpirationTime
                        ? "#d32f2f"
                        : "green",
                  },
              }}
            />
            <DatePicker
              label={t("userSystem.endDate") + " *"}
              value={formData.expirationTime}
              format="YYYY-MM-DD"
              disabled={formData.noExpirationTime}
              onChange={(event) => {
                handleChange({
                  target: { name: "expirationTime", value: event },
                });
              }}
              minDate={formData.startTime as any}
              slotProps={{
                textField: {
                  margin: "normal",
                  size: "small",
                  color: "success",
                  sx: { width: 160 },
                  error:
                    isError &&
                    !formData.expirationTime &&
                    !formData.noExpirationTime,
                },
              }}
              sx={{
                "& .MuiInputLabel-root": {
                  color:
                    isError &&
                    !formData.expirationTime &&
                    !formData.noExpirationTime
                      ? "#d32f2f"
                      : "",
                },
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    isError &&
                    !formData.expirationTime &&
                    !formData.noExpirationTime
                      ? "#d32f2f"
                      : "",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color:
                    isError &&
                    !formData.expirationTime &&
                    !formData.noExpirationTime
                      ? "#d32f2f"
                      : "green",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      isError &&
                      !formData.expirationTime &&
                      !formData.noExpirationTime
                        ? "#d32f2f"
                        : "green",
                  },
              }}
            />
          </LocalizationProvider>
        </div>

        <div className="flex gap-4 items-center mt-4">
          {formData.notificationType === "Release" && (
            <FormControl
              size="small"
              sx={{ minWidth: 180 }}
              error={isError && !formData.releaseVersion}
            >
              <InputLabel color="success" required>
                {t("userSystem.releaseVersion")}
              </InputLabel>
              <Select
                value={formData.releaseVersion || ""}
                onChange={(event) => {
                  const selectedVersion = event.target.value;
                  const selectedRelease = (props.releaseVersions || []).find(
                    (r) => r.version === selectedVersion
                  );
                  setFormData((prevData) => ({
                    ...prevData,
                    releaseVersion: selectedVersion,
                    releaseId: selectedRelease?.id,
                  }));
                }}
                name="releaseVersion"
                label={t("userSystem.releaseVersion")}
                color="success"
                disabled={
                  !props.releaseVersions || props.releaseVersions.length === 0
                }
                sx={{ height: 40 }}
              >
                {(props.releaseVersions || []).map((release) => (
                  <MenuItem key={release.id} value={release.version}>
                    <ListItemText primary={release.version} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel size="small" color="success">
              {t("userSystem.status")}
            </InputLabel>
            <Select
              size="small"
              value={formData.status}
              onChange={handleChange}
              name="status"
              label={t("userSystem.status")}
              color="success"
              disabled={!props.data}
              sx={{ height: 40 }}
            >
              <MenuItem value="draft">
                <ListItemText primary={t("userSystem.statusDraft")} />
              </MenuItem>
              <MenuItem value="publish">
                <ListItemText primary={t("userSystem.statusPublish")} />
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className="flex gap-4 items-center mt-4">
          <FormControlLabel
            sx={{ marginLeft: 0 }}
            control={
              <Checkbox
                checked={formData.noExpirationTime}
                onChange={handleChange}
                name="noExpirationTime"
                color="success"
              />
            }
            label={t("userSystem.hasUnlimitedTime")}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isBanner}
                onChange={handleChange}
                name="isBanner"
                color="success"
              />
            }
            label={t("userSystem.isBannerMessage")}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isRing}
                onChange={handleChange}
                name="isRing"
                color="success"
              />
            }
            label={t("userSystem.isRingMessage")}
          />
        </div>

        <TextField
          label={t("userSystem.messageContent")}
          variant="outlined"
          margin="normal"
          name="notificationMessage"
          color={isContentOverLimit ? "error" : "success"}
          value={formData.notificationMessage}
          onChange={handleChange}
          fullWidth
          multiline
          rows={4}
          error={isContentOverLimit}
          helperText={`${formData.notificationMessage.length}/${maxLength}`}
          FormHelperTextProps={{
            sx: { color: isContentOverLimit ? "#d32f2f" : undefined },
          }}
        />
      </DialogContent>
      <DialogActions
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
        }}
      >
        <Button onClick={reset} color="success">
          {t("buttons.reset")}
        </Button>

        <div style={{ marginLeft: "auto" }}>
          <Button onClick={close} color="success">
            {t("buttons.cancel")}
          </Button>
          <Button onClick={handleSave} color="success" variant="contained">
            {t("buttons.save")}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default MessageEditModal;
